import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/Header";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, ArrowRight } from "lucide-react";

const STAGES = ["Idea", "Pre-launch", "Early revenue", "Growing", "Established"];
const TYPES = ["SaaS", "Ecommerce", "Freelancer", "Agency", "Consultancy", "Restaurant / Hospitality", "Education", "Creator business", "Other"];
const GOALS = ["Grow revenue", "Find first customers", "Reduce churn", "Raise prices", "Ship faster", "Improve conversion", "Get repeatable acquisition", "Cut wasted time"];

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: user?.first_name || "", business_name: user?.business_name || "", business_type: user?.business_type || "",
    industry: user?.industry || "", stage: user?.stage || "", goals: user?.goals || [],
  });
  const [ctxOpen, setCtxOpen] = useState(false);
  const [ctx, setCtx] = useState({
    what_they_sell: "", typical_customer: "", pricing: "",
    acquisition_channel: "", business_model: "", current_challenge: "",
    ...(user?.context || {}),
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleGoal = (g) => set("goals", form.goals.includes(g) ? form.goals.filter((x) => x !== g) : [...form.goals, g]);

  const submit = async () => {
    setBusy(true);
    try {
      const context = Object.fromEntries(Object.entries(ctx).filter(([, v]) => (v || "").trim()));
      const { data } = await api.post("/onboarding", { ...form, context: Object.keys(context).length ? context : undefined });
      setUser(data);
      toast.success("Context saved. Let's clear a blocker.");
      navigate("/app");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not save");
    } finally { setBusy(false); }
  };

  const inputCls = "mt-1.5 border-white/12 bg-[var(--surface-raised)] text-white";

  return (
    <div className="relative min-h-screen px-5 py-10" data-testid="onboarding-page">
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 flex justify-center"><BrandMark /></div>
        <div className="rounded-2xl border border-white/10 bg-[var(--surface)] p-7 sm:p-10">
          <span className="eyebrow">Optional · 30-second setup</span>
          <h1 className="font-head mt-2 text-3xl font-black tracking-tight text-white">Tell us just enough.</h1>
          <p className="mt-2 text-sm text-neutral-400">Context sharpens every move MinuteSwap gives you. Skip anything you like.</p>

          <div className="mt-8 space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <Label className="text-neutral-300">First name</Label>
                <Input data-testid="onb-firstname" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className={inputCls} />
              </div>
              <div>
                <Label className="text-neutral-300">Business / project <span className="text-neutral-600">(optional)</span></Label>
                <Input data-testid="onb-business" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} className={inputCls} placeholder="Northlight Analytics" />
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <Label className="text-neutral-300">Business type</Label>
                <Select value={form.business_type} onValueChange={(v) => set("business_type", v)}>
                  <SelectTrigger data-testid="onb-type" className={inputCls}><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent className="border-white/12 bg-[var(--surface-overlay)] text-white">
                    {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-neutral-300">Industry</Label>
                <Input data-testid="onb-industry" value={form.industry} onChange={(e) => set("industry", e.target.value)} className={inputCls} placeholder="B2B analytics" />
              </div>
            </div>

            <div>
              <Label className="text-neutral-300">Current stage</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {STAGES.map((s) => (
                  <button key={s} data-testid={`onb-stage-${s.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => set("stage", s)}
                    className={`rounded-full border px-4 py-1.5 text-sm transition-all ${form.stage === s ? "border-[var(--amber)] bg-[var(--amber)]/10 text-white" : "border-white/12 text-neutral-400 hover:border-white/30"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-neutral-300">Primary goals</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {GOALS.map((g) => (
                  <button key={g} data-testid={`onb-goal-${g.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => toggleGoal(g)}
                    className={`rounded-full border px-4 py-1.5 text-sm transition-all ${form.goals.includes(g) ? "border-[var(--amber)] bg-[var(--amber)]/10 text-white" : "border-white/12 text-neutral-400 hover:border-white/30"}`}>
                    {g}
                  </button>
                ))}
              </div>
            </div>

            <button data-testid="onb-context-toggle" onClick={() => setCtxOpen(!ctxOpen)}
              className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-[var(--surface-raised)] px-4 py-3 text-left">
              <div>
                <p className="text-sm font-semibold text-white">Business context <span className="font-normal text-neutral-500">(optional)</span></p>
                <p className="text-xs text-neutral-500">Helps MinuteSwap reason like it knows your business.</p>
              </div>
              {ctxOpen ? <ChevronUp className="h-4 w-4 text-neutral-400" /> : <ChevronDown className="h-4 w-4 text-neutral-400" />}
            </button>

            {ctxOpen && (
              <div className="grid gap-4 rounded-xl border border-white/10 bg-[var(--surface-raised)]/50 p-4 sm:grid-cols-2">
                {[
                  ["what_they_sell", "What you sell"],
                  ["typical_customer", "Typical customer"],
                  ["pricing", "Pricing"],
                  ["acquisition_channel", "Main acquisition channel"],
                  ["business_model", "Business model"],
                  ["current_challenge", "Current challenge"],
                ].map(([k, label]) => (
                  <div key={k} className={k === "current_challenge" ? "sm:col-span-2" : ""}>
                    <Label className="text-xs text-neutral-400">{label}</Label>
                    <Input data-testid={`onb-ctx-${k}`} value={ctx[k]} onChange={(e) => setCtx({ ...ctx, [k]: e.target.value })} className={inputCls} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-8 flex items-center justify-between">
            <button data-testid="onb-skip" onClick={() => navigate("/app")} className="text-sm text-neutral-500 hover:text-neutral-300">Skip for now</button>
            <Button data-testid="onb-submit" onClick={submit} disabled={busy} className="btn-amber rounded-full px-6">
              {busy ? "Saving…" : <>Enter the Action Engine <ArrowRight className="ml-1 h-4 w-4" /></>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
